import { useEffect, useRef, useState } from "react"
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
]

const buildYearOptions = () => {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let year = currentYear + 1; year >= 2020; year -= 1) {
    years.push(year)
  }
  return years
}

const PAYMENT_STATUSES = [
  { value: "", label: "All Payment Statuses" },
  { value: "pending", label: "Pending" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "fully_paid", label: "Fully Paid" },
  { value: "overpaid", label: "Overpaid" },
  { value: "refunded", label: "Refunded" },
]

const MR_STATUSES = [
  { value: "", label: "All MR Statuses" },
  { value: "active", label: "Active" },
  { value: "cancelled", label: "Cancelled" },
  { value: "refunded", label: "Refunded" },
]

const MoneyReceiptExportModal = ({
  isOpen,
  toggle,
  onExport,
  isExporting = false,
  defaultDeliveryStatus = "",
}) => {
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [deliveryStatus, setDeliveryStatus] = useState(defaultDeliveryStatus)
  const [paymentStatus, setPaymentStatus] = useState("")
  const [mrStatus, setMrStatus] = useState("")
  const touchedRef = useRef(false)

  useEffect(() => {
    if (isOpen && !touchedRef.current) {
      setDeliveryStatus(defaultDeliveryStatus)
    }
  }, [isOpen, defaultDeliveryStatus])

  const handleSubmit = event => {
    event.preventDefault()
    touchedRef.current = true

    const lastDay = new Date(year, month, 0).getDate()
    const monthLabel = String(month).padStart(2, "0")

    const params = {
      start_date: `${year}-${monthLabel}-01`,
      end_date: `${year}-${monthLabel}-${String(lastDay).padStart(2, "0")}`,
      delivery_status: deliveryStatus,
      payment_status: paymentStatus,
      status: mrStatus,
    }

    Object.keys(params).forEach(key => {
      if (params[key] === "") delete params[key]
    })

    onExport(params)
  }

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="Export Money Receipts"
      size="md"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button color="light" onClick={toggle} disabled={isExporting}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={isExporting}>
            {isExporting ? "Exporting..." : "Export Excel"}
          </Button>
        </div>
      }
    >
      <Form onSubmit={handleSubmit}>
        <Row className="gy-2">
          <Col xs={12} sm={6}>
            <FormGroup>
              <Label for="mr-export-month" className="fw-semibold">
                Month
              </Label>
              <Input
                id="mr-export-month"
                type="select"
                value={month}
                onChange={e => setMonth(Number(e.target.value))}
                disabled={isExporting}
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col xs={12} sm={6}>
            <FormGroup>
              <Label for="mr-export-year" className="fw-semibold">
                Year
              </Label>
              <Input
                id="mr-export-year"
                type="select"
                value={year}
                onChange={e => setYear(Number(e.target.value))}
                disabled={isExporting}
              >
                {buildYearOptions().map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col xs={12} sm={6}>
            <FormGroup>
              <Label for="mr-export-delivery" className="fw-semibold">
                Delivery Status
              </Label>
              <Input
                id="mr-export-delivery"
                type="select"
                value={deliveryStatus}
                onChange={e => setDeliveryStatus(e.target.value)}
                disabled={isExporting}
              >
                <option value="">All</option>
                <option value="delivered">Delivered</option>
                <option value="not_delivered">Not Delivered</option>
              </Input>
            </FormGroup>
          </Col>
          <Col xs={12} sm={6}>
            <FormGroup>
              <Label for="mr-export-payment" className="fw-semibold">
                Payment Status
              </Label>
              <Input
                id="mr-export-payment"
                type="select"
                value={paymentStatus}
                onChange={e => setPaymentStatus(e.target.value)}
                disabled={isExporting}
              >
                {PAYMENT_STATUSES.map(ps => (
                  <option key={ps.value || "all"} value={ps.value}>
                    {ps.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col xs={12} sm={6}>
            <FormGroup>
              <Label for="mr-export-status" className="fw-semibold">
                Money Receipt Status
              </Label>
              <Input
                id="mr-export-status"
                type="select"
                value={mrStatus}
                onChange={e => setMrStatus(e.target.value)}
                disabled={isExporting}
              >
                {MR_STATUSES.map(ms => (
                  <option key={ms.value || "all"} value={ms.value}>
                    {ms.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <small className="text-muted d-block mt-2">
          Exports all money receipts matching the selected filters as an Excel-compatible CSV for audit and reporting.
        </small>
      </Form>
    </ThemeModal>
  )
}

export default MoneyReceiptExportModal